import { EyeInvisibleOutlined, EyeOutlined, FileTextOutlined } from "@ant-design/icons";
import { Alert, App, Button, Descriptions, Drawer, Space, Table, Tabs, Tag, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useCandidate, useSetEnabled } from "../../api/candidates";
import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import { usePermission } from "../../auth/SessionProvider";

export function CandidateDrawer({ username, onClose }: { username?: string; onClose: () => void }) {
  const detail = useCandidate(username);
  const { groupName, regionName, siteName, roomName } = useNames();
  const setEnabled = useSetEnabled();
  const { message } = App.useApp();
  const [showPassword, setShowPassword] = useState(false);
  const canDisable = usePermission("candidate:disable");
  const canEnable = usePermission("candidate:enable");
  const canReadScores = usePermission("score:read");
  const navigate = useNavigate();
  const data = detail.data;

  const toggle = async () => {
    if (!data) return;
    const result = await setEnabled.mutateAsync({ usernames: [data.username], enabled: !data.enabled });
    message.success(result.updated === 1 ? (data.enabled ? "账号已关闭" : "账号已开放") : "操作失败");
  };

  return (
    <Drawer
      open={username !== undefined}
      onClose={onClose}
      width={760}
      title={data ? `${data.name}（${data.username}）` : username}
      extra={data && (
        <Space>
          {canReadScores && (
            <Button icon={<FileTextOutlined />} onClick={() => { onClose(); navigate(`/m/answer-sheets?username=${encodeURIComponent(data.username)}`); }}>
              查看答卷
            </Button>
          )}
          {(data.enabled ? canDisable : canEnable) && (
            <Button danger={data.enabled} type={data.enabled ? "default" : "primary"} loading={setEnabled.isPending} onClick={() => void toggle()}>
              {data.enabled ? "关闭账号" : "开放账号"}
            </Button>
          )}
        </Space>
      )}
    >
      {detail.error && <Alert type="error" showIcon message={detail.error.message} />}
      {data && (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="准考证号">{data.username}</Descriptions.Item>
            <Descriptions.Item label="姓名">{data.name}</Descriptions.Item>
            <Descriptions.Item label="组别">{groupName(data.group)}</Descriptions.Item>
            <Descriptions.Item label="省份">{data.province ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="考点区域">{regionName(data.regionId)}</Descriptions.Item>
            <Descriptions.Item label="考点">{siteName(data.siteId)}（{data.siteId}）</Descriptions.Item>
            <Descriptions.Item label="考场 / 座位">{roomName(data.roomId)} / {data.seat ?? "—"}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {data.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="密码">
              <Space>
                <Typography.Text code>{showPassword ? data.password : "••••••••"}</Typography.Text>
                <Button
                  type="text"
                  size="small"
                  icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() => setShowPassword((value) => !value)}
                />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="试卷">
              {data.exam ? data.exam.title : <Tag color="orange">试卷版本不一致</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="最近登录">{formatTime(data.lastLoginAt)} {data.lastLoginIp ?? ""}</Descriptions.Item>
            <Descriptions.Item label="最近提交">{formatTime(data.lastSubmitAt)} {data.lastSubmitIp ?? ""}</Descriptions.Item>
          </Descriptions>
          <Tabs
            items={[
              {
                key: "logins",
                label: `登录记录（${data.loginHistory.length}）`,
                children: (
                  <Table
                    rowKey="at"
                    size="small"
                    pagination={false}
                    dataSource={data.loginHistory}
                    columns={[{ title: "时间", dataIndex: "at", render: formatTime }, { title: "IP", dataIndex: "ip" }]}
                  />
                )
              },
              {
                key: "submits",
                label: `提交记录（${data.submitHistory.length}）`,
                children: (
                  <Table
                    rowKey="at"
                    size="small"
                    pagination={false}
                    dataSource={data.submitHistory}
                    columns={[{ title: "时间", dataIndex: "at", render: formatTime }, { title: "IP", dataIndex: "ip" }]}
                  />
                )
              },
              {
                key: "status",
                label: `状态变更（${data.enabledHistory.length}）`,
                children: (
                  <Table
                    rowKey="at"
                    size="small"
                    pagination={false}
                    dataSource={data.enabledHistory}
                    columns={[
                      { title: "时间", dataIndex: "at", render: formatTime },
                      { title: "状态", dataIndex: "enabled", render: (value: boolean) => value ? <Tag color="green">启用</Tag> : <Tag color="red">关闭</Tag> }
                    ]}
                  />
                )
              }
            ]}
          />
        </Space>
      )}
    </Drawer>
  );
}
